// The Revise room's state and writes in the browser (the Mac's LibraryModel+Revise, +Style, +Cleanup and +Revisions):
// this device's choices (findings left, checks switched off or on, the pass worked on, revision marks shown), the last
// style check's findings for the session, Checks' book (the whole manuscript's findings, worked out in the Revise worker)
// and the finding chosen in it, and every write the room makes — each one `live.edit`, through the core's
// `ReviseEditor`, so it syncs like any edit and records the checkpoints the Mac records. Text open in Write is never
// rewritten under the editor: it goes through the editor (`reviseEditor.ts`), undoably.
import { storageId } from './storageId.ts';
import { useSyncExternalStore } from 'react';
import {
  utf16OffsetOfScalar, type DocumentEditor, type InkColor, type OpenedProject, type Project,
} from '@writing-suite/core';
import {
  currentRevision, findingPass, isCheckOn, projectStyleSheet, spellingDictionary, styleSheetMarkdown, usualDashStyle, makeChoice, readCommentCategories, reviseBook, type CommentCategory, isRevisionActive, readRevisions, ReviseEditor,
  styleSettings, COLUMN_PASSES, OFF_BY_DEFAULT, type BuiltInStyleCheck, type DashStyle, type NumberStyle, type PassageFix,
  type ReviseBook, type ReviseFinding, type RevisePass, type ReviseSwitches, type Revision, type RevisionChange, type Revisions, type StyleFinding,
  type VariantChoice, type VariantSet,
} from '@writing-suite/core/revise';
import { quoteStyleOf, type QuoteStyle } from '@writing-suite/core/text';
import { authorName } from './useProject.ts';
import { readGlossary, spellingWords } from '@writing-suite/core/story';
import type { SentBook } from './reviseHost.ts';
import { storyEdit } from './story.ts';
import type { LiveProject } from './project.ts';
import { reviseClient } from './reviseClient.ts';
import { openEditor, openFragments } from './reviseEditor.ts';

// MARK: This device's choices (localStorage; failures ignored — they're conveniences)

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // kept for this page only
  }
}

let prefsVersion = 0;
const prefListeners = new Set<() => void>();
const prefsChanged = () => {
  prefsVersion++;
  for (const fn of prefListeners) fn();
};

/** Re-renders when any of these choices changes (in this tab). */
export function useRevisePrefs(): number {
  return useSyncExternalStore(
    (fn) => {
      prefListeners.add(fn);
      return () => prefListeners.delete(fn);
    },
    () => prefsVersion,
  );
}

/** `dismissedFindings-<project>`: the findings the writer chose to leave, on this device. */
export const dismissedKey = (projectId: string) => `dismissedFindings-${storageId(projectId)}`;

export function dismissedFindings(projectId: string): Set<string> {
  const v = read<unknown>(dismissedKey(projectId), []);
  return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
}

export function dismissFindings(projectId: string, ids: readonly string[]) {
  const all = dismissedFindings(projectId);
  for (const id of ids) all.add(id);
  write(dismissedKey(projectId), [...all].sort());
  prefsChanged();
}

/** Forgets dismissed ids that match nothing any more (`pruneDismissed`). */
export function forgetDismissed(projectId: string, stale: readonly string[]) {
  if (!stale.length) return;
  const all = dismissedFindings(projectId);
  for (const id of stale) all.delete(id);
  write(dismissedKey(projectId), [...all].sort());
}

const checkSet = (key: string): Set<string> => {
  const v = read<unknown>(key, '');
  return new Set(typeof v === 'string' ? v.split('|').filter(Boolean) : []);
};

/** `reviseHiddenChecks`: checks switched off on this device. */
export const hiddenChecks = () => checkSet('ws:reviseHiddenChecks');
/** `reviseShownChecks`: checks off by default (underlining) switched on, on this device. */
export const shownChecks = () => checkSet('ws:reviseShownChecks');
export const reviseSwitches = (): ReviseSwitches => ({ hidden: hiddenChecks(), shown: shownChecks() });

/** Switches a check that isn't the project's (a built-in style check's switch is) on or off, on this device. */
export function toggleCheck(check: string) {
  const key = OFF_BY_DEFAULT.has(check) ? 'ws:reviseShownChecks' : 'ws:reviseHiddenChecks';
  const all = checkSet(key);
  if (!all.delete(check)) all.add(check);
  write(key, [...all].sort().join('|'));
  prefsChanged();
}

/** `revisePass`: the pass the margin works on — a pass, "all", or "" (the first with anything left). */
export const chosenPass = () => {
  const v = read<unknown>('ws:revisePass', '');
  return typeof v === 'string' ? v : '';
};
export function setChosenPass(pass: RevisePass | 'all') {
  write('ws:revisePass', pass);
  prefsChanged();
}

/** `showsRevisionMarks`, per device. */
export const showsRevisionMarks = () => read<boolean>('ws:showsRevisionMarks', true) !== false;
export function setShowsRevisionMarks(on: boolean) {
  write('ws:showsRevisionMarks', on);
  prefsChanged();
}

// MARK: The style check (the session's findings, as the Mac keeps `styleFindings` in memory)

export interface StyleRun {
  /** The last check's findings (null: none has run this session). */
  findings: StyleFinding[] | null;
  /** While a check runs. */
  progress: string | null;
  error: string | null;
}

const runs = new Map<string, StyleRun>();
let runsVersion = 0;
const runListeners = new Set<() => void>();
const runsChanged = () => {
  runsVersion++;
  for (const fn of runListeners) fn();
};
const EMPTY_RUN: StyleRun = { findings: null, progress: null, error: null };

export const styleRun = (projectId: string): StyleRun => runs.get(projectId) ?? EMPTY_RUN;

export function useStyleRun(projectId: string): StyleRun {
  useSyncExternalStore(
    (fn) => {
      runListeners.add(fn);
      return () => runListeners.delete(fn);
    },
    () => runsVersion,
  );
  return styleRun(projectId);
}

/** Findings the writer has dealt with (added to the margin): gone from this session's list. */
export function dropStyleFindings(projectId: string, ids: ReadonlySet<string>) {
  const r = runs.get(projectId);
  if (!r?.findings) return;
  runs.set(projectId, { ...r, findings: r.findings.filter((f) => !ids.has(f.id)) });
  runsChanged();
}

const running = new Map<string, number>();

/** Runs the switched-on built-in checks over the manuscript, in the Revise worker. */
export async function runStyleCheck(live: LiveProject, project: OpenedProject): Promise<void> {
  const id = project.id;
  const settings = styleSettings(project.record);
  const names = project.entities().flatMap((e) => [e.name, ...e.aliases]);
  const token = (running.get(id) ?? 0) + 1;
  running.set(id, token);
  runs.set(id, { ...styleRun(id), progress: 'Checking…', error: null });
  runsChanged();
  try {
    await live.flush();
    const findings = await reviseClient(live).style([...settings.builtIns], names);
    if (running.get(id) !== token) return;
    runs.set(id, { findings, progress: null, error: null });
  } catch (e) {
    if (running.get(id) !== token) return;
    runs.set(id, { ...styleRun(id), progress: null, error: e instanceof Error ? e.message : String(e) });
  }
  runsChanged();
}

export function cancelStyleCheck(projectId: string) {
  running.set(projectId, (running.get(projectId) ?? 0) + 1);
  runs.set(projectId, { ...styleRun(projectId), progress: null });
  runsChanged();
}

// MARK: Checks' book (the Mac's `reviseBook`, worked out in the Revise worker after each change)

export interface BookState {
  /** The last book worked out (null: none yet), and whether its words were checked for spelling. */
  book: (ReviseBook & Partial<Pick<SentBook, 'spelling'>>) | null;
  /** The finding chosen, and a count bumped each time one is chosen from the margin or stepped to (the reader scrolls). */
  selected: string | null;
  reveal: number;
  error: string | null;
  /** A Fix all waiting for the writer's yes, and whether the style sheet is open (the column asks; the page shows them). */
  fixAll: FixAllRequest | null;
  styleSheet: boolean;
}

const books = new Map<string, BookState>();
let booksVersion = 0;
const bookListeners = new Set<() => void>();
const booksChanged = () => {
  booksVersion++;
  for (const fn of bookListeners) fn();
};
const EMPTY_BOOK: BookState = { book: null, selected: null, reveal: 0, error: null, fixAll: null, styleSheet: false };
export const bookState = (projectId: string): BookState => books.get(projectId) ?? EMPTY_BOOK;
const setBookState = (projectId: string, patch: Partial<BookState>) => {
  books.set(projectId, { ...bookState(projectId), ...patch });
  booksChanged();
};

export function useBookState(projectId: string): BookState {
  useSyncExternalStore(
    (fn) => {
      bookListeners.add(fn);
      return () => bookListeners.delete(fn);
    },
    () => booksVersion,
  );
  return bookState(projectId);
}

export const askFixAll = (projectId: string, request: FixAllRequest | null) => setBookState(projectId, { fixAll: request });
export const showStyleSheet = (projectId: string, open: boolean) => setBookState(projectId, { styleSheet: open });

/** Keeps a newly worked-out book (`setReviseBook`). A finding's id is made from the passage around it, so an edit nearby
 *  (a fix) gives the chosen one a new id: it stays chosen if the same check still marks the same words in its scene. */
export function keepBook(projectId: string, book: ReviseBook & Partial<Pick<SentBook, 'spelling'>>) {
  const state = bookState(projectId);
  const chosen = state.book?.findings.find((f) => f.id === state.selected);
  let selected = state.selected;
  if (chosen && !book.findings.some((f) => f.id === chosen.id)) {
    const near = book.findings
      .filter((f) => f.fragmentID === chosen.fragmentID && f.check === chosen.check && f.quote === chosen.quote)
      .sort((x, y) => Math.abs(x.range[0] - chosen.range[0]) - Math.abs(y.range[0] - chosen.range[0]))[0];
    selected = near?.id ?? null;
  }
  forgetDismissed(projectId, book.staleDismissed);
  setBookState(projectId, { book, selected, error: null });
}

const working = new Map<string, number>();

/** The spelling check's dictionary for the project's language and the words the writer knows here (the glossary and
 *  the cast: the personal dictionary is in iCloud, on the Apple devices); null when the web has no dictionary for it. */
export function spellingRequest(project: OpenedProject) {
  const dictionary = spellingDictionary(project.record.locale);
  return dictionary ? { dictionary, known: [...spellingWords(readGlossary(project.record), project.record.entities)] } : null;
}

/** Works the book out again (in the Revise worker), with this session's style findings and this device's dismissals. */
export async function refreshBook(live: LiveProject, project: OpenedProject): Promise<void> {
  const projectId = project.id;
  const token = (working.get(projectId) ?? 0) + 1;
  working.set(projectId, token);
  try {
    const book = await reviseClient(live).book(styleRun(projectId).findings, [...dismissedFindings(projectId)], null, spellingRequest(project));
    if (working.get(projectId) === token) keepBook(projectId, book);
  } catch (e) {
    if (working.get(projectId) === token) setBookState(projectId, { error: e instanceof Error ? e.message : String(e) });
  }
}

/** The book worked out in this thread (tests, and a page with no live project). */
export function bookNow(project: OpenedProject, styleFindings: readonly StyleFinding[] | null = styleRun(project.id).findings): ReviseBook {
  return reviseBook(project, { styleFindings, dismissed: dismissedFindings(project.id) });
}

/** The findings Revise ▸ Checks shows, in reading order (`shownFindings`): those whose check is switched on, in `pass`
 *  when one is given, dismissed ones and personal rules' left out (habits are checked on the Mac and iPad). */
export function shownFindings(book: ReviseBook | null, project: OpenedProject, pass: RevisePass | null = null): ReviseFinding[] {
  if (!book) return [];
  const switches = reviseSwitches();
  const settings = styleSettings(project.record);
  const dismissed = dismissedFindings(project.id);
  return book.findings.filter((f) => !dismissed.has(f.id) && !f.check.startsWith('rule:') && (pass === null || findingPass(f) === pass)
    && isCheckOn(f.check, switches, settings));
}

/** The pass the margin works on (`revisePass(chosen:in:)`): the one chosen, else the first with anything left (null: all). */
export function revisePassOf(chosen: string, shown: readonly ReviseFinding[]): RevisePass | null {
  if (chosen === 'all') return null;
  if ((COLUMN_PASSES as readonly string[]).includes(chosen)) return chosen as RevisePass;
  return COLUMN_PASSES.find((p) => shown.some((f) => findingPass(f) === p)) ?? null;
}

/** The findings the margin steps through: those shown in the pass it works on. */
export function steppedFindings(book: ReviseBook | null, project: OpenedProject): ReviseFinding[] {
  const all = shownFindings(book, project);
  const pass = revisePassOf(chosenPass(), all);
  return pass ? all.filter((f) => findingPass(f) === pass) : all;
}

/** Chooses a finding (null: none). `reveal`: the reader and the margin bring it into view (chosen from the margin or
 *  stepped to; an underline chosen in the text leaves the page where it is). */
export function chooseFinding(projectId: string, id: string | null, reveal = true) {
  const state = bookState(projectId);
  setBookState(projectId, { selected: id, reveal: reveal && id ? state.reveal + 1 : state.reveal });
}

/** The finding after (or before) the chosen one among `shown`; with none chosen, the first from the unit being read on,
 *  or the last up to it (`neighbouringFinding`). */
export function neighbouringFinding(book: ReviseBook | null, shown: readonly ReviseFinding[], selected: string | null, forward: boolean, unitID: string | null): ReviseFinding | null {
  const i = selected ? shown.findIndex((f) => f.id === selected) : -1;
  if (i >= 0) return shown[forward ? i + 1 : i - 1] ?? null;
  const order = new Map((book?.units ?? []).map((u, k) => [u, k]));
  const here = (unitID ? order.get(unitID) : undefined) ?? 0;
  const place = (f: ReviseFinding) => (f.unitID ? order.get(f.unitID) ?? 0 : 0);
  return forward ? shown.find((f) => place(f) >= here) ?? shown[0] ?? null : [...shown].reverse().find((f) => place(f) <= here) ?? shown.at(-1) ?? null;
}

/** Once the chosen finding is dealt with, the next one stepped through is chosen (else the one before), so the writer can
 *  work down the list (`chooseNext(after:)`). */
export function chooseNext(project: OpenedProject, finding: ReviseFinding) {
  const state = bookState(project.id);
  if (state.selected !== finding.id) return;
  const shown = steppedFindings(state.book, project);
  const i = shown.findIndex((f) => f.id === finding.id);
  const next = i < 0 ? null : shown[i + 1] ?? shown[i - 1] ?? null;
  chooseFinding(project.id, next?.id ?? null);
}

/** Checks' ‹ › while the page is shown, for Project ▸ Next Finding (⌘') and Previous Finding (⇧⌘'). */
let stepper: ((forward: boolean) => void) | null = null;
export function setFindingStepper(fn: ((forward: boolean) => void) | null) {
  stepper = fn;
}
export const findingStepper = () => stepper;

/** Leave it: hidden on this device, and the next one chosen. */
export function leaveFinding(project: OpenedProject, finding: ReviseFinding) {
  chooseNext(project, finding);
  dismissFindings(project.id, [finding.id]);
}

// MARK: Writes

/** The core's Revise writer over the live documents (one `live.edit`: what it changes syncs and is shown). */
export function reviseEdit<T>(live: LiveProject, fn: (r: ReviseEditor) => T): T {
  return live.edit((e) => {
    const d = e as unknown as DocumentEditor;
    return fn(new ReviseEditor(d.docs, d.context));
  });
}

/** A finding's Fix: through the editor when its scene is open (undoable), else in the document. */
export function fixFinding(live: LiveProject, finding: ReviseFinding): boolean {
  if (finding.replacement === undefined) return false;
  const e = openEditor(live);
  const text = e?.text(finding.fragmentID);
  if (e && text !== null && text !== undefined) {
    const from = utf16OffsetOfScalar(text, finding.range[0]), to = utf16OffsetOfScalar(text, finding.range[1]);
    if (text.slice(from, to) !== finding.quote) return false;
    return e.replaceRange(finding.fragmentID, from, to, finding.replacement);
  }
  return reviseEdit(live, (r) => r.fixFinding(finding));
}

/** Add to margin: the finding as a comment from its check (once), then left. */
export function addToMargin(live: LiveProject, projectId: string, findings: readonly ReviseFinding[]): number {
  const added = reviseEdit(live, (r) => r.addFindingsToMargin(findings));
  dismissFindings(projectId, findings.map((f) => f.id));
  dropStyleFindings(projectId, new Set(findings.flatMap((f) => (f.style ? [f.style.id] : []))));
  return added;
}

/** "Add All to Margin" for the list's style results. */
export function addStyleToMargin(live: LiveProject, projectId: string, findings: readonly StyleFinding[]): number {
  return reviseEdit(live, (r) => r.addStyleToMargin(findings));
}

export function setBuiltInStyleCheck(live: LiveProject, check: BuiltInStyleCheck, enabled: boolean) {
  reviseEdit(live, (r) => r.setBuiltInStyleCheck(check, enabled));
}

export function setDashStyle(live: LiveProject, style: DashStyle | null) {
  reviseEdit(live, (r) => r.setDashStyle(style));
}

// MARK: Fix all, the style sheet and consistency

/** A Fix all waiting for the writer's yes (`FixAllRequest`): what it changes, and the checkpoint recorded first. */
export interface FixAllRequest { title: string; checkpoint: string; findings: ReviseFinding[] }

/** Every typography fix shown. */
export const fixAllTypography = (shown: readonly ReviseFinding[]): FixAllRequest => ({
  title: 'Fix All Typography', checkpoint: 'Before fixing typography',
  findings: shown.filter((f) => findingPass(f) === 'typography' && f.replacement !== undefined),
});

/** Every fix shown for one check. */
export const fixAllOf = (check: string, name: string, shown: readonly ReviseFinding[]): FixAllRequest => ({
  title: `Fix All: ${name}`, checkpoint: `Before fixing ${name.toLowerCase()}`, findings: shown.filter((f) => f.check === check && f.replacement !== undefined),
});

/**
 * Makes these fixes after a checkpoint (`DocumentStore.applyFixes`): exactly the passages shown. A scene open in Write
 * changes through the editor (one undoable step, made of small edits); the rest in the documents. Returns how many.
 */
export function applyFixes(live: LiveProject, fixes: readonly PassageFix[], checkpoint: string): number {
  if (!fixes.length) return 0;
  const e = openEditor(live);
  const open = new Set(e?.fragmentIds() ?? []);
  const closed = fixes.filter((f) => !open.has(f.fragmentID));
  let made = closed.length ? reviseEdit(live, (r) => r.applyFixes(closed, checkpoint)) : 0;
  const byOpen = new Map<string, PassageFix[]>();
  for (const f of fixes) if (open.has(f.fragmentID)) byOpen.set(f.fragmentID, [...(byOpen.get(f.fragmentID) ?? []), f]);
  const changes: [string, string, number][] = [];
  for (const [id, list] of byOpen) {
    const text = e!.text(id);
    if (text === null) continue;
    const scalars = [...text];
    let n = 0, end = Infinity;
    for (const fix of [...list].sort((a, b) => b.range[0] - a.range[0])) {
      const [lo, hi] = fix.range;
      if (hi > end || hi > scalars.length || scalars.slice(lo, hi).join('') !== fix.quote) continue;
      scalars.splice(lo, hi - lo, ...fix.replacement);
      end = lo;
      n++;
    }
    if (n) changes.push([id, scalars.join(''), n]);
  }
  if (changes.length && made === 0) reviseEdit(live, (r) => r.createCheckpoint(checkpoint, 'manual'));
  for (const [id, text, n] of changes) if (e!.replaceText(id, text)) made += n;
  return made;
}

/** Fix all: every one of these findings that has a fix. */
export function fixAll(live: LiveProject, projectId: string, findings: readonly ReviseFinding[], checkpoint: string): number {
  const fixes = findings.flatMap((f) => (f.replacement !== undefined ? [{ fragmentID: f.fragmentID, range: f.range, quote: f.quote, replacement: f.replacement }] : []));
  const made = applyFixes(live, fixes, checkpoint);
  const state = bookState(projectId);
  if (findings.some((f) => f.id === state.selected)) chooseFinding(projectId, null);
  return made;
}

/** A consistency set made whole (`useVariant`): every place in the other form becomes `keep` (after "Before using “x”
 *  throughout"), and the choice goes in the style sheet, so a later one is caught; with `keep` null both are kept and the
 *  set isn't raised again. Numbers: the style sheet's number style. Returns how many places changed. */
export function applyVariant(live: LiveProject, projectId: string, book: ReviseBook, set: VariantSet, keep: string | null): number {
  let made = 0;
  if (keep !== null) {
    const fixes = set.occurrences.flatMap((x) => (x.form.toLowerCase() !== keep.toLowerCase() && x.alternative !== null && book.sceneOrder[x.text]
      ? [{ fragmentID: book.sceneOrder[x.text], range: x.range, quote: x.written, replacement: x.alternative }] : []));
    made = applyFixes(live, fixes, set.kind === 'numbers' ? 'Before spelling out numbers' : `Before using “${keep}” throughout`);
  }
  if (set.kind === 'numbers') setNumberStyle(live, keep === null ? 'digits' : 'words');
  else setStyleChoice(live, makeChoice(set.kind, set.forms, keep));
  chooseFinding(projectId, null);
  return made;
}

export function setStyleChoice(live: LiveProject, choice: VariantChoice) {
  reviseEdit(live, (r) => r.putStyleChoice(choice));
}

export function removeStyleChoice(live: LiveProject, choice: VariantChoice) {
  reviseEdit(live, (r) => r.deleteStyleChoice(choice));
}

export function setDialogueQuotes(live: LiveProject, style: QuoteStyle | null) {
  reviseEdit(live, (r) => r.setDialogueQuotes(style));
}

export function setNumberStyle(live: LiveProject, style: NumberStyle | null) {
  reviseEdit(live, (r) => r.setNumberStyle(style));
}

/** The style sheet as a copy-editor would hand it on (the style sheet's Export…): `styleSheetMarkdown` with the marks and
 *  dashes the book is checked against, and the name it's published under (the project's pen name, else the writer's). */
export function styleSheetDocument(project: OpenedProject): string {
  const record = project.record;
  const sheet = projectStyleSheet(record);
  const texts = project.counted(project.targets().countAllText).map((id) => project.fragment(id)?.text ?? '');
  let penName: string | null = null;
  try {
    const raw = record.settings.penName;
    const v = typeof raw === 'string' ? (JSON.parse(raw) as { name?: unknown }) : null;
    if (v && typeof v.name === 'string') penName = v.name;
  } catch {
    // unreadable: the writer's own name
  }
  return styleSheetMarkdown({
    title: record.title, author: penName ?? authorName(), sheet, dialogue: sheet.dialogueQuotes ?? quoteStyleOf(texts),
    dashes: sheet.dashStyle ?? usualDashStyle(texts), locale: record.locale, entities: record.entities, glossary: readGlossary(record),
  });
}

/** A word the spelling check didn't know joins the project's glossary (a name if it's capitalised): known from now on. */
export function addWordToGlossary(live: LiveProject, word: string): boolean {
  return storyEdit(live, (e) => e.addToGlossary(word)).length > 0;
}

// MARK: Comment categories

/** The project's comment categories, by name. */
export const commentCategoriesOf = (project: Project): CommentCategory[] => {
  const record = (project as OpenedProject).record;
  return record ? readCommentCategories(record) : [];
};

/** What a refused category edit says (a name that's empty, too long or another category's), else null. */
function refusal(fn: () => void): string | null {
  try {
    fn();
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}

/** Adds a category in the first colour none has. Returns why not, when the name can't be used. */
export function addCommentCategory(live: LiveProject, name: string, ink?: InkColor): string | null {
  return refusal(() => reviseEdit(live, (r) => r.addCommentCategory(name, ink)));
}

/** Renames or recolours a category (as it's stored now: the view's copy may be a moment old); every comment in it
 *  follows. Returns why not, when the name can't be used. */
export function updateCommentCategory(live: LiveProject, id: string, patch: { name?: string; color?: InkColor }): string | null {
  return refusal(() => reviseEdit(live, (r) => r.updateCommentCategory(id, patch)));
}

/** Removes a category; its comments stay, in no category. */
export function deleteCommentCategory(live: LiveProject, category: CommentCategory) {
  reviseEdit(live, (r) => r.deleteCommentCategory(category));
}

/** Puts a comment (on the text or the synopsis) in a category, or with null in none. */
export function setCommentCategory(live: LiveProject, fragmentId: string, commentId: string, categoryId: string | null) {
  live.edit((e) => e.setCommentCategory(fragmentId, commentId, categoryId));
}

// MARK: Revision mode

export const revisionsOf = (project: Project): Revisions => readRevisions((project as OpenedProject).record);
export const revisionActive = (project: Project) => isRevisionActive(revisionsOf(project));
export const currentRevisionOf = (project: Project): Revision | null => currentRevision(revisionsOf(project));

/** The name the next revision would take: "Second draft" after one, and so on. */
export function suggestedRevisionName(project: Project): string {
  const ordinals = ['Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
  const n = revisionsOf(project).revisions.length;
  return n < ordinals.length ? `${ordinals[n]} draft` : `Revision ${n + 1}`;
}

/** Start Revision: a "Start of …" checkpoint (or an earlier one) and the revision; the marks show. */
export async function startRevision(live: LiveProject, name: string, fromCheckpoint: string | null): Promise<Revision> {
  await live.flush();
  const revision = reviseEdit(live, (r) => r.startRevision(name, fromCheckpoint));
  if (!showsRevisionMarks()) setShowsRevisionMarks(true);
  return revision;
}

/** Accept: the change stays, unmarked (a base patch; the text isn't touched). */
export function acceptChange(live: LiveProject, change: RevisionChange, fragmentId: string, current: string): boolean {
  return reviseEdit(live, (r) => r.acceptRevisionChanges([change], fragmentId, current)) > 0;
}

/** Accept All: the marks go and revision mode ends, after an "Accepted revisions (…)" checkpoint. */
export async function acceptAllRevisions(live: LiveProject) {
  await live.flush();
  reviseEdit(live, (r) => r.finishRevisions());
}

/**
 * Reject All: every changed document back to its base (accepted changes kept), after "Before rejecting revisions";
 * open ones through the editor (one undoable step each). Returns how many documents were put back, and how many open
 * ones the editor refused.
 */
export async function rejectAllRevisions(live: LiveProject, project: OpenedProject): Promise<{ changed: number; refused: number }> {
  await live.flush();
  const open = openFragments(live);
  const { changed, openBases } = reviseEdit(live, (r) => r.rejectAllRevisions(project.fragmentIds(), open));
  const e = openEditor(live);
  let refused = 0, applied = 0;
  for (const [id, base] of Object.entries(openBases)) {
    if (e?.replaceText(id, base)) applied++;
    else refused++;
  }
  return { changed: changed + applied, refused };
}
